import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useNotices } from '../contexts/NoticeContext';

const NoticeList: React.FC = () => {
  const { notices } = useNotices();
  const [searchKeyword, setSearchKeyword] = useState('');

  // 페이지 진입 시 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredNotices = notices.filter(notice => {
    // 비공개 공지 제외
    if (!notice.is_published) return false;

    return notice.title.toLowerCase().includes(searchKeyword.toLowerCase());
  });

  // 공지가 활성 상태인지 확인 (마감기한이 아직 지나지 않았는지)
  const isNoticeActive = (notice: any) => {
    if (!notice.is_important) return false;
    if (!notice.notice_end_date) return true; // 마감기한이 없으면 계속 활성

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const endDate = new Date(notice.notice_end_date);

    return endDate >= now;
  };

  return (
    <>
      <SEO
        title="공지사항"
        description="동두천시 글로벌인재교육센터의 공지사항을 확인하세요. 수강 안내, 휴강 공지, 이벤트 소식 등을 확인할 수 있습니다."
        keywords="공지사항, 동두천글로벌인재교육센터, 수강안내, 교육센터소식"
        path="/notice"
      />
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">공지사항</h1>
            <p className="text-gray-600">센터의 소식과 공지사항을 확인하세요.</p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="검색어를 입력하세요"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Notice List */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    담당부서
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작성일
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredNotices.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      등록된 공지사항이 없습니다.
                    </td>
                  </tr>
                ) : (
                  filteredNotices.map((notice, index) => (
                    <tr key={notice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {isNoticeActive(notice) ? (
                          <span className="text-blue-600 font-semibold">공지</span>
                        ) : (
                          index + 1
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/notice/${notice.id}`}
                          className="text-sm text-gray-900 hover:text-blue-600 font-medium"
                        >
                          {notice.title}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {notice.department}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(notice.created_at).toLocaleDateString('ko-KR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default NoticeList;
